import { useEffect, useState } from 'react';
import AdventurerStamp, { ADVENTURERS, type AdventurerId } from '../components/adventurer/AdventurerStamp';
import { getLearnSettings, updateLearnSettings, updatePreferences, updateChildPin, updateChildProfile } from '../api/client';
import type { LearnSettings, StudentProfile, SessionLength, DifficultyPreference } from '../api/types';
import { useAudioFirst } from '../hooks/useAudioFirst';
import Logo from '../components/brand/Logo';
import AddChildSection from '../components/parent/AddChildSection';
import { useSounds } from '../hooks/useSounds';

interface Props {
  profile: StudentProfile | null;
  onBack: () => void;
  onSaved: (updated: { adventurer_id: string; preferences: StudentProfile['preferences']; interests: string[] }) => void;
  /** Called after a sibling was added, so the app can refresh its child list. */
  onChildAdded?: () => void;
}

const SESSION_OPTIONS: { value: SessionLength; label: string; sub: string }[] = [
  { value: 'kurz', label: 'Kurz', sub: '5 Aufgaben' },
  { value: 'mittel', label: 'Mittel', sub: '10 Aufgaben' },
  { value: 'lang', label: 'Lang', sub: '15 Aufgaben' },
];

const DIFFICULTY_OPTIONS: { value: DifficultyPreference; label: string; sub: string }[] = [
  { value: 'leicht', label: 'Leicht', sub: 'Einfachere Aufgaben' },
  { value: 'mittel', label: 'Mittel', sub: 'Ausgewogen' },
  { value: 'schwer', label: 'Schwer', sub: 'Herausfordernd' },
  { value: 'auto', label: 'Automatisch', sub: 'KI entscheidet' },
];

// Curriculum-backed options for the parent focus picker.
// Mirrors curriculum/klasse2_mathe.py + klasse2_deutsch.py labels for the most
// commonly requested focus topics. Up to 2 can be active at a time for 20 exercises.
const FOCUS_TOPIC_OPTIONS: { value: string; label: string; subject: string }[] = [
  { value: 'einmaleins',          label: 'Kleines Einmaleins',   subject: 'Mathe' },
  { value: 'addition_bis_100',    label: 'Addition bis 100',     subject: 'Mathe' },
  { value: 'subtraktion_bis_100', label: 'Subtraktion bis 100',  subject: 'Mathe' },
  { value: 'verdoppeln_halbieren', label: 'Verdoppeln/Halbieren', subject: 'Mathe' },
  { value: 'zeitrechnung',        label: 'Uhrzeit lesen',        subject: 'Mathe' },
  { value: 'sachaufgaben',        label: 'Sachaufgaben',         subject: 'Mathe' },
  { value: 'rechtschreibung',     label: 'Rechtschreibung',      subject: 'Deutsch' },
  { value: 'lesen_sinnverstehen', label: 'Lesen verstehen',      subject: 'Deutsch' },
];

export default function SettingsScreen({ profile, onBack, onSaved, onChildAdded }: Props) {
  const prefs = profile?.preferences;
  const [adventurerId, setAdventurerId] = useState<AdventurerId>(
    (profile?.story_state?.adventurer_id as AdventurerId) || 'finn'
  );
  const [sessionLength, setSessionLength] = useState<SessionLength>(prefs?.session_length || 'lang');
  const [difficulty, setDifficulty] = useState<DifficultyPreference>(prefs?.difficulty_preference || 'auto');
  const [focusTopics, setFocusTopics] = useState<string[]>(prefs?.focus_topics || []);
  const [interestsRaw, setInterestsRaw] = useState<string>((profile?.interests || []).join(', '));
  const focusRemaining = prefs?.focus_topics_remaining ?? 0;
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const audioFirst = useAudioFirst();
  const sounds = useSounds();
  const [learn, setLearn] = useState<LearnSettings | null>(null);
  const [learnSaving, setLearnSaving] = useState(false);

  // ── Kind bearbeiten + PIN ändern (PR 3) ─────────────────────────────────
  const [childName, setChildName] = useState<string>(profile?.name || '');
  const [childKlasse, setChildKlasse] = useState<number>(profile?.klasse || 2);
  const [childSaving, setChildSaving] = useState(false);
  const [childMsg, setChildMsg] = useState<string | null>(null);
  const [newPin, setNewPin] = useState<string>('');
  const [pinSaving, setPinSaving] = useState(false);
  const [pinMsg, setPinMsg] = useState<string | null>(null);

  const handleSaveChild = async () => {
    if (!profile?.student_id) return;
    const name = childName.trim();
    if (!name) { setChildMsg('Name darf nicht leer sein.'); return; }
    setChildSaving(true); setChildMsg(null);
    try {
      await updateChildProfile(profile.student_id, { name, klasse: childKlasse });
      setChildMsg('✓ Gespeichert.');
    } catch (e) {
      setChildMsg(e instanceof Error ? e.message : 'Fehler beim Speichern.');
    } finally {
      setChildSaving(false);
    }
  };

  const handleSavePin = async (clear: boolean) => {
    if (!profile?.student_id) return;
    if (!clear && !/^[0-9]{4}$/.test(newPin)) {
      setPinMsg('PIN muss genau 4 Ziffern haben.'); return;
    }
    setPinSaving(true); setPinMsg(null);
    try {
      await updateChildPin(profile.student_id, clear ? null : newPin);
      setPinMsg(clear ? '✓ PIN entfernt.' : '✓ Neuer PIN gespeichert.');
      setNewPin('');
    } catch (e) {
      setPinMsg(e instanceof Error ? e.message : 'Fehler beim Speichern.');
    } finally {
      setPinSaving(false);
    }
  };

  useEffect(() => {
    let alive = true;
    getLearnSettings()
      .then(({ settings }) => { if (alive) setLearn(settings); })
      .catch(() => { /* non-fatal — keep section hidden if it fails */ });
    return () => { alive = false; };
  }, []);

  async function patchLearn(patch: Partial<LearnSettings>) {
    if (!learn) return;
    const optimistic = { ...learn, ...patch };
    setLearn(optimistic);
    setLearnSaving(true);
    try {
      const { settings } = await updateLearnSettings(patch);
      setLearn(settings);
    } catch (e) {
      console.error(e);
      setLearn(learn);
    } finally {
      setLearnSaving(false);
    }
  }

  const CAP_OPTIONS = [3, 5, 10, 25, 50, 100];

  function toggleFocusTopic(value: string) {
    setFocusTopics((prev) => {
      if (prev.includes(value)) return prev.filter((t) => t !== value);
      if (prev.length >= 2) return prev;   // cap at 2
      return [...prev, value];
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      const interests = interestsRaw
        .split(/[,\n]/)
        .map(t => t.trim())
        .filter(Boolean)
        .slice(0, 5);
      const result = await updatePreferences({
        adventurer_id: adventurerId,
        session_length: sessionLength,
        difficulty_preference: difficulty,
        focus_topics: focusTopics,
        interests,
      });
      onSaved(result);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      minHeight: '100dvh',
      background: 'var(--parchment, #fdf6e3)',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'var(--serif, Georgia, serif)',
    }}>
      {/* Header — sticks at the top of the viewport so the back button is
          always reachable. The whole page scrolls underneath it; mouse-wheel
          works anywhere on the page (not only inside the inner column). */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '16px 20px',
        borderBottom: '1px solid rgba(120,90,60,0.15)',
        background: 'var(--parchment, #fdf6e3)',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 20, color: 'var(--ink-muted, #8a7a6a)',
            padding: '4px 8px',
          }}
          aria-label="Zurück"
        >
          ←
        </button>
        <Logo variant="wordmark" height={26} />
        <span style={{
          fontSize: 17, fontWeight: 600, color: 'var(--ink, #3c3228)',
          paddingLeft: 12,
          borderLeft: '1px solid rgba(120,90,60,0.2)',
        }}>
          Einstellungen
        </span>
      </div>

      <div style={{
        padding: '20px 16px calc(32px + env(safe-area-inset-bottom, 0px))',
        maxWidth: 480, margin: '0 auto', width: '100%',
      }}>

        {/* Abenteurer */}
        <section style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-muted, #8a7a6a)', margin: '0 0 12px' }}>
            Abenteurer
          </h2>
          <div style={{ display: 'flex', gap: 12 }}>
            {(Object.values(ADVENTURERS)).map((a) => (
              <button
                key={a.id}
                onClick={() => setAdventurerId(a.id)}
                style={{
                  flex: 1, border: '2px solid', borderRadius: 14, padding: '12px 8px',
                  cursor: 'pointer', background: adventurerId === a.id ? `${a.color}18` : '#fff',
                  borderColor: adventurerId === a.id ? a.color : 'rgba(120,90,60,0.2)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  transition: 'all 0.15s',
                }}
              >
                <AdventurerStamp id={a.id} size={52} active={adventurerId === a.id} />
                <span style={{ fontSize: 13, fontWeight: 600, color: adventurerId === a.id ? a.color : 'var(--ink, #3c3228)' }}>
                  {a.name}
                </span>
                <span style={{ fontSize: 10, color: 'var(--ink-muted, #8a7a6a)' }}>{a.tagline}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Sitzungslänge */}
        <section style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-muted, #8a7a6a)', margin: '0 0 12px' }}>
            Sitzungslänge
          </h2>
          <div style={{ display: 'flex', gap: 10 }}>
            {SESSION_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSessionLength(opt.value)}
                style={{
                  flex: 1, border: '2px solid', borderRadius: 12, padding: '14px 8px',
                  cursor: 'pointer',
                  background: sessionLength === opt.value ? '#e8f0fe' : '#fff',
                  borderColor: sessionLength === opt.value ? '#4a6ef5' : 'rgba(120,90,60,0.2)',
                  transition: 'all 0.15s',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 700, color: sessionLength === opt.value ? '#4a6ef5' : 'var(--ink, #3c3228)' }}>
                  {opt.label}
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-muted, #8a7a6a)', marginTop: 3 }}>{opt.sub}</div>
              </button>
            ))}
          </div>
        </section>

        {/* Schwierigkeit */}
        <section style={{ marginBottom: 36 }}>
          <h2 style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-muted, #8a7a6a)', margin: '0 0 12px' }}>
            Schwierigkeit
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {DIFFICULTY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setDifficulty(opt.value)}
                style={{
                  border: '2px solid', borderRadius: 12, padding: '14px 10px',
                  cursor: 'pointer',
                  background: difficulty === opt.value ? '#fef3e2' : '#fff',
                  borderColor: difficulty === opt.value ? '#f5a623' : 'rgba(120,90,60,0.2)',
                  transition: 'all 0.15s',
                  textAlign: 'left',
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 700, color: difficulty === opt.value ? '#c97d10' : 'var(--ink, #3c3228)' }}>
                  {opt.label}
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-muted, #8a7a6a)', marginTop: 3 }}>{opt.sub}</div>
              </button>
            ))}
          </div>
        </section>

        {/* Eltern-Fokus */}
        <section style={{ marginBottom: 36 }}>
          <h2 style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-muted, #8a7a6a)', margin: '0 0 8px' }}>
            Eltern-Fokus
          </h2>
          <p style={{ fontSize: 12, color: 'var(--ink-muted, #8a7a6a)', margin: '0 0 12px', lineHeight: 1.4 }}>
            Wähle 1–2 Themen, die in den nächsten 20 Aufgaben häufiger drankommen
            (z.B. vor einer Klassenarbeit). Danach wählt die KI wieder frei.
            {focusRemaining > 0 && focusTopics.length > 0 && (
              <> · noch <strong>{focusRemaining}</strong> Aufgaben aktiv</>
            )}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {FOCUS_TOPIC_OPTIONS.map((opt) => {
              const active = focusTopics.includes(opt.value);
              const disabled = !active && focusTopics.length >= 2;
              return (
                <button
                  key={opt.value}
                  onClick={() => toggleFocusTopic(opt.value)}
                  disabled={disabled}
                  style={{
                    border: '2px solid', borderRadius: 12, padding: '10px 10px',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    background: active ? '#eaf7ee' : '#fff',
                    borderColor: active ? '#4caf50' : 'rgba(120,90,60,0.2)',
                    opacity: disabled ? 0.5 : 1,
                    textAlign: 'left', transition: 'all 0.15s',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 700, color: active ? '#2e7d32' : 'var(--ink, #3c3228)' }}>
                    {active ? '✓ ' : ''}{opt.label}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--ink-muted, #8a7a6a)', marginTop: 2 }}>{opt.subject}</div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Interessen */}
        <section style={{ marginBottom: 36 }}>
          <h2 style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-muted, #8a7a6a)', margin: '0 0 8px' }}>
            Interessen
          </h2>
          <p style={{ fontSize: 12, color: 'var(--ink-muted, #8a7a6a)', margin: '0 0 10px', lineHeight: 1.4 }}>
            Bis zu 5 Themen, die Finn in Sachaufgaben einweben darf — durch Komma getrennt.
          </p>
          <input
            type="text"
            value={interestsRaw}
            onChange={(e) => setInterestsRaw(e.target.value)}
            placeholder="z.B. Dinosaurier, Pferde, Fußball"
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '12px 14px',
              border: '1.5px solid rgba(120,90,60,0.25)',
              borderRadius: 10,
              fontFamily: 'var(--sans, system-ui)', fontSize: 15,
              color: 'var(--ink, #3c3228)', background: '#fff',
            }}
          />
        </section>

        {/* Zugänglichkeit */}
        {(audioFirst.available || sounds.available) && (
          <section style={{ marginBottom: 36 }}>
            <h2 style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-muted, #8a7a6a)', margin: '0 0 8px' }}>
              Zugänglichkeit
            </h2>
            {audioFirst.available && (
              <label style={{
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                padding: '14px 16px', border: '1.5px solid rgba(120,90,60,0.2)',
                borderRadius: 12, background: '#fff', cursor: 'pointer', gap: 12,
                marginBottom: 10,
              }}>
                <span style={{ flex: 1 }}>
                  <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--ink, #3c3228)' }}>
                    Audio-First-Modus
                  </span>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-muted, #8a7a6a)', marginTop: 4, lineHeight: 1.4 }}>
                    Für Kinder, die noch nicht flüssig lesen können. Finn liest jede Frage
                    und jede Antwort automatisch vor — auch wenn das Vorlesen sonst aus ist.
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={audioFirst.enabled}
                  onChange={(e) => audioFirst.setEnabled(e.target.checked)}
                  style={{ width: 22, height: 22, cursor: 'pointer', marginTop: 2 }}
                />
              </label>
            )}
            {sounds.available && (
              <label style={{
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                padding: '14px 16px', border: '1.5px solid rgba(120,90,60,0.2)',
                borderRadius: 12, background: '#fff', cursor: 'pointer', gap: 12,
              }}>
                <span style={{ flex: 1 }}>
                  <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--ink, #3c3228)' }}>
                    Töne
                  </span>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-muted, #8a7a6a)', marginTop: 4, lineHeight: 1.4 }}>
                    Kleine Klick-, Richtig- und Kapitel-Töne beim Antworten. Nutzt keine
                    Audiodateien — alles wird im Browser erzeugt.
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={sounds.enabled}
                  onChange={() => sounds.toggle()}
                  style={{ width: 22, height: 22, cursor: 'pointer', marginTop: 2 }}
                />
              </label>
            )}
          </section>
        )}

        {/* Frag-Finn Modus */}
        {learn && (
          <section style={{ marginBottom: 36 }}>
            <h2 style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-muted, #8a7a6a)', margin: '0 0 8px' }}>
              Frag-Finn Modus
            </h2>
            <p style={{ fontSize: 12, color: 'var(--ink-muted, #8a7a6a)', margin: '0 0 14px', lineHeight: 1.4 }}>
              Hier kann das Kind Finn eigene Fragen stellen. Finn antwortet altersgerecht,
              heikle Themen (Tod, Gewalt, …) leitet er an Mama/Papa weiter.
            </p>

            <label style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 16px', border: '1.5px solid rgba(120,90,60,0.2)',
              borderRadius: 12, background: '#fff', cursor: 'pointer', marginBottom: 10,
            }}>
              <span>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--ink, #3c3228)' }}>
                  Frag-Finn erlauben
                </span>
                <span style={{ fontSize: 11, color: 'var(--ink-muted, #8a7a6a)' }}>
                  Wenn aus: Frag-Finn-Knopf verschwindet im Story-Screen.
                </span>
              </span>
              <input
                type="checkbox"
                checked={learn.enabled}
                disabled={learnSaving}
                onChange={(e) => patchLearn({ enabled: e.target.checked })}
                style={{ width: 22, height: 22, cursor: 'pointer' }}
              />
            </label>

            <div style={{ marginTop: 14, marginBottom: 8, fontSize: 12, fontWeight: 600, color: 'var(--ink, #3c3228)' }}>
              Heikle Themen
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
              {(['default', 'strict'] as const).map((mode) => {
                const active = learn.sensitive_topic_mode === mode;
                return (
                  <button
                    key={mode}
                    onClick={() => patchLearn({ sensitive_topic_mode: mode })}
                    disabled={learnSaving || !learn.enabled}
                    style={{
                      border: '2px solid', borderRadius: 12, padding: '12px 10px',
                      cursor: learn.enabled ? 'pointer' : 'not-allowed',
                      background: active ? '#fef3e2' : '#fff',
                      borderColor: active ? '#f5a623' : 'rgba(120,90,60,0.2)',
                      opacity: learn.enabled ? 1 : 0.5, textAlign: 'left',
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 700, color: active ? '#c97d10' : 'var(--ink, #3c3228)' }}>
                      {mode === 'default' ? 'Ausgewogen' : 'Streng'}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--ink-muted, #8a7a6a)', marginTop: 2 }}>
                      {mode === 'default'
                        ? 'Tod/Krieg altersgerecht + Hinweis an Eltern'
                        : 'Finn weicht bei heiklen Themen immer aus'}
                    </div>
                  </button>
                );
              })}
            </div>

            <div style={{ marginTop: 14, marginBottom: 8, fontSize: 12, fontWeight: 600, color: 'var(--ink, #3c3228)' }}>
              Fragen pro Tag
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6, marginBottom: 10 }}>
              {CAP_OPTIONS.map((cap) => {
                const active = learn.daily_cap === cap;
                return (
                  <button
                    key={cap}
                    onClick={() => patchLearn({ daily_cap: cap })}
                    disabled={learnSaving || !learn.enabled}
                    style={{
                      border: '2px solid', borderRadius: 10, padding: '10px 4px',
                      cursor: learn.enabled ? 'pointer' : 'not-allowed',
                      background: active ? '#e8f0fe' : '#fff',
                      borderColor: active ? '#4a6ef5' : 'rgba(120,90,60,0.2)',
                      opacity: learn.enabled ? 1 : 0.5,
                      fontSize: 14, fontWeight: 700,
                      color: active ? '#4a6ef5' : 'var(--ink, #3c3228)',
                    }}
                  >
                    {cap}
                  </button>
                );
              })}
            </div>

            {/* No email toggle: alerts are shown in the Elternbereich only (decision 2026-09-25). */}
            <p style={{ fontSize: 12, color: 'var(--ink-muted, #8a7a6a)', margin: '8px 0 0', lineHeight: 1.5 }}>
              Wenn Finn Hinweise auf ernste Themen erkennt, siehst du das beim nächsten Öffnen des
              Elternbereichs ganz oben. Es wird keine E-Mail verschickt.
            </p>
          </section>
        )}

        {/* ── Kind bearbeiten ────────────────────────────────────────────── */}
        <section style={{
          marginTop: 24, padding: 20, background: '#fff',
          borderRadius: 14, border: '1.5px solid rgba(120,90,60,0.15)',
        }}>
          <div style={{
            fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.18em',
            color: 'var(--ink-muted, #8a7a6a)', textTransform: 'uppercase', marginBottom: 10,
          }}>Kind bearbeiten</div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Name</label>
          <input
            value={childName}
            onChange={(e) => setChildName(e.target.value)}
            maxLength={40}
            style={{
              width: '100%', padding: '10px 14px', fontSize: 15,
              border: '1.5px solid rgba(120,90,60,0.2)', borderRadius: 8,
              background: '#fff', boxSizing: 'border-box', fontFamily: 'inherit',
            }}
          />
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '14px 0 6px' }}>Klasse</label>
          <select
            value={childKlasse}
            onChange={(e) => setChildKlasse(Number(e.target.value))}
            style={{
              width: '100%', padding: '10px 14px', fontSize: 15,
              border: '1.5px solid rgba(120,90,60,0.2)', borderRadius: 8,
              background: '#fff', boxSizing: 'border-box', fontFamily: 'inherit',
            }}
          >
            {[1, 2, 3, 4, 5, 6].map((k) => (
              <option key={k} value={k}>Klasse {k}</option>
            ))}
          </select>
          {childMsg && (
            <div style={{ marginTop: 10, fontSize: 12, color: childMsg.startsWith('✓') ? '#2e7d32' : 'var(--wrong)' }}>
              {childMsg}
            </div>
          )}
          <button
            onClick={handleSaveChild}
            disabled={childSaving}
            style={{
              marginTop: 14, padding: '10px 18px', borderRadius: 10, border: 'none',
              background: '#4a6ef5', color: '#fff', fontSize: 14, fontWeight: 700,
              cursor: childSaving ? 'wait' : 'pointer', fontFamily: 'inherit',
            }}
          >
            {childSaving ? 'Speichere…' : 'Änderungen speichern'}
          </button>
        </section>

        <AddChildSection onAdded={() => onChildAdded?.()} />

        {/* ── PIN ändern ────────────────────────────────────────────────── */}
        <section style={{
          marginTop: 16, padding: 20, background: '#fff',
          borderRadius: 14, border: '1.5px solid rgba(120,90,60,0.15)',
        }}>
          <div style={{
            fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.18em',
            color: 'var(--ink-muted, #8a7a6a)', textTransform: 'uppercase', marginBottom: 10,
          }}>PIN ändern</div>
          <div style={{ fontSize: 13, color: 'var(--ink-muted, #8a7a6a)', marginBottom: 10 }}>
            Der 4-stellige Kinder-PIN wird beim Wechseln des Kindprofils verlangt.
          </div>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            value={newPin}
            onChange={(e) => setNewPin(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="Neuer PIN (4 Ziffern)"
            style={{
              width: '100%', padding: '10px 14px', fontSize: 18,
              border: '1.5px solid rgba(120,90,60,0.2)', borderRadius: 8,
              background: '#fff', boxSizing: 'border-box', fontFamily: 'var(--display, inherit)',
              letterSpacing: '0.2em', textAlign: 'center',
            }}
          />
          {pinMsg && (
            <div style={{ marginTop: 10, fontSize: 12, color: pinMsg.startsWith('✓') ? '#2e7d32' : 'var(--wrong)' }}>
              {pinMsg}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button
              onClick={() => handleSavePin(false)}
              disabled={pinSaving || newPin.length !== 4}
              style={{
                flex: 1, padding: '10px 18px', borderRadius: 10, border: 'none',
                background: '#4a6ef5', color: '#fff', fontSize: 14, fontWeight: 700,
                cursor: pinSaving || newPin.length !== 4 ? 'not-allowed' : 'pointer',
                opacity: pinSaving || newPin.length !== 4 ? 0.5 : 1,
                fontFamily: 'inherit',
              }}
            >
              {pinSaving ? 'Speichere…' : 'PIN setzen'}
            </button>
            <button
              onClick={() => handleSavePin(true)}
              disabled={pinSaving}
              style={{
                flex: 1, padding: '10px 18px', borderRadius: 10,
                border: '1.5px solid rgba(120,90,60,0.25)',
                background: '#fff', color: 'var(--ink, #3c3228)', fontSize: 14, fontWeight: 600,
                cursor: pinSaving ? 'wait' : 'pointer', fontFamily: 'inherit',
              }}
            >
              PIN entfernen
            </button>
          </div>
        </section>

        {/* Save */}
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            width: '100%', padding: '16px', borderRadius: 14, border: 'none',
            background: saved ? '#4caf50' : '#4a6ef5',
            color: '#fff', fontSize: 16, fontWeight: 700, cursor: saving ? 'wait' : 'pointer',
            transition: 'background 0.2s',
            fontFamily: 'var(--serif, Georgia, serif)',
          }}
        >
          {saving ? 'Wird gespeichert…' : saved ? '✓ Gespeichert!' : 'Einstellungen speichern'}
        </button>
      </div>
    </div>
  );
}
